'use client';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations as useTranslations$1 } from 'next-intl';
import Bowser from 'bowser';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { trackBrowserWarningDismissed, trackBrowserWarningViewed } from '@/lib/analytics';
import { detectTabletDevice } from '@/hooks/useIsTablet';
const STORAGE_KEY = 'martini-browser-warning-dismissed';
const SUPPRESSED_PATH_PREFIXES = [
    '/m/',
    '/welcome'
];
function useIsClient(): boolean {
    return useSyncExternalStore(()=>()=>{}, ()=>true, ()=>false);
}
function shouldSuppressWarning(pathname: string | null): boolean {
    if (!pathname) return false;
    return SUPPRESSED_PATH_PREFIXES.some((prefix)=>prefix.endsWith('/') ? pathname.startsWith(prefix) : pathname === prefix || pathname.startsWith(`${prefix}/`));
}
function getBrowserContext(): {
    browser: string;
    engine: string;
    os: string;
    platform: string;
} {
    if (typeof navigator === 'undefined') {
        return {
            browser: 'unknown',
            engine: 'unknown',
            os: 'unknown',
            platform: 'unknown'
        };
    }
    const parser = Bowser.getParser(navigator.userAgent);
    return {
        browser: parser.getBrowserName() || 'unknown',
        engine: parser.getEngineName() || 'unknown',
        os: parser.getOSName() || 'unknown',
        platform: parser.getPlatformType() || 'unknown'
    };
}
function hasDismissedWarning(): boolean {
    if (typeof localStorage === 'undefined') return true;
    try {
        return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch  {
        return true;
    }
}
/**
 * Detects if the current browser is using Chromium/Blink engine.
 * Returns true for Chrome, Chromium-based browsers (Brave, Arc, Vivaldi, etc.).
 * Returns false for other engines (Firefox/Gecko, Safari/WebKit).
 */ function isChromiumBrowser(): boolean {
    if (typeof navigator === 'undefined') return true // SSR: assume Chromium
    ;
    const browser = Bowser.getParser(navigator.userAgent);
    const engineName = browser.getEngineName();
    // Chromium/Blink engine includes: Chrome, Brave, Arc, Vivaldi, Edge, Opera
    // We only want to show the warning for non-Chromium engines (WebKit, Gecko, etc.)
    return engineName === 'Blink';
}
/**
 * A dialog that appears for non-Chrome users, recommending Chrome
 * for optimal performance. Dismissible and remembers the choice in localStorage.
 */ export function BrowserWarning() {
    const t = useTranslations$1();
    const pathname = usePathname();
    const isTablet = typeof navigator !== 'undefined' && detectTabletDevice(navigator.userAgent, navigator.maxTouchPoints);
    const isClient = useIsClient();
    const [dismissedInSession, setDismissedInSession] = useState(false);
    const viewedRef = useRef(false);
    const suppressed = isTablet || shouldSuppressWarning(pathname);
    const open = isClient && !suppressed && !dismissedInSession && !isChromiumBrowser() && !hasDismissedWarning();
    useEffect(()=>{
        if (open && !viewedRef.current) {
            viewedRef.current = true;
            trackBrowserWarningViewed({
                pathname: pathname ?? '/',
                ...getBrowserContext()
            });
        }
    }, [
        open,
        pathname
    ]);
    const handleDismiss = ()=>{
        try {
            localStorage.setItem(STORAGE_KEY, 'true');
        } catch  {
        // Non-critical: session state still hides the modal for this tab.
        }
        trackBrowserWarningDismissed({
            pathname: pathname ?? '/',
            ...getBrowserContext()
        });
        setDismissedInSession(true);
    };
    if (!open) return null;
    return (<Dialog open={open} onOpenChange={(isOpen)=>{
        if (!isOpen) handleDismiss();
    }}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e)=>e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t("HAAkQz")}</DialogTitle>
          <DialogDescription>{t("iBeSbj")}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={handleDismiss}>{t("acrOoz")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);
}
